/**
 * Smart Domain Pack Wizard
 * AI-powered multi-step wizard for creating domain packs based on company context
 *
 * Phase 3+ Implementation (November 2025)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Building2,
  CheckCircle2,
  Loader2,
  Sparkles,
  AlertCircle,
  Search,
  ListPlus,
} from 'lucide-react';

import { suggestDomains, generateDomainPack } from '../../lib/api/domainPacksApi';
import { standaloneCompaniesAPI, api } from '../../lib/api';
import { INDUSTRIES } from '../../lib/constants';
import { useResearchQueueStore } from '../../stores/researchQueueStore';
import { toast } from 'sonner';
import Button from '../ui/Button';
import { Card, CardHeader, CardBody } from '../ui/Card';
import Input from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Label } from '../ui/Label';
import { WizardSubdomainSelectModal } from './WizardSubdomainSelectModal';

// ========================================
// Types
// ========================================

interface CompanyContext {
  company_name: string;
  industry: string;
  business_unit: string;
}

interface DomainSuggestion {
  domain_pack_id: string | null;
  version_id: string | null;
  domain_name: string;
  industry: string;
  industry_display: string;
  research_available: boolean;
  variant_id: string | null;
  relevance_score: number;
  reasoning: string;
  total_subdomains: number;
  total_hypotheses: number;
  quality_score: number;
  relevant_subdomains: any[];
  top_hypotheses: any[];
}

interface SECCompany {
  cik: string;
  name: string;
  ticker: string;
  exchange: string;
  match_type: string;
}

type WizardStep = 'context' | 'suggestions' | 'subdomains';

// ========================================
// Helper Functions
// ========================================

/**
 * Map SEC industry (from SIC codes) to our industry categories
 * SEC industries are very specific, we need to map them to our broader categories
 */
const mapSECIndustryToCategory = (secIndustry: string): string => {
  const industry = secIndustry.toLowerCase();

  // Financial Services
  if (industry.includes('bank') || industry.includes('insurance') ||
      industry.includes('investment') || industry.includes('securities') ||
      industry.includes('finance') || industry.includes('credit')) {
    return 'financial-services';
  }

  // Healthcare
  if (industry.includes('pharmaceutical') || industry.includes('medical') ||
      industry.includes('hospital') || industry.includes('health') ||
      industry.includes('drug') || industry.includes('biotech')) {
    return 'healthcare';
  }

  // Retail
  if (industry.includes('retail') || industry.includes('store') ||
      industry.includes('grocery') || industry.includes('apparel')) {
    return 'retail';
  }

  // Manufacturing
  if (industry.includes('manufacturing') || industry.includes('industrial') ||
      industry.includes('machinery') || industry.includes('equipment') ||
      industry.includes('automotive') || industry.includes('aerospace')) {
    return 'manufacturing';
  }

  // Technology
  if (industry.includes('computer') || industry.includes('software') ||
      industry.includes('technology') || industry.includes('semiconductor') ||
      industry.includes('electronic') || industry.includes('internet') ||
      industry.includes('data processing')) {
    return 'technology';
  }

  // Telecommunications
  if (industry.includes('telecommunication') || industry.includes('telephone') ||
      industry.includes('wireless') || industry.includes('cable')) {
    return 'telecommunications';
  }

  // Energy & Utilities
  if (industry.includes('energy') || industry.includes('utility') ||
      industry.includes('electric') || industry.includes('gas') ||
      industry.includes('oil') || industry.includes('petroleum')) {
    return 'energy';
  }

  // Transportation & Logistics
  if (industry.includes('transport') || industry.includes('airline') ||
      industry.includes('railroad') || industry.includes('shipping') ||
      industry.includes('logistics') || industry.includes('freight')) {
    return 'transportation';
  }

  // Media & Entertainment
  if (industry.includes('media') || industry.includes('entertainment') ||
      industry.includes('broadcast') || industry.includes('motion picture') ||
      industry.includes('publishing')) {
    return 'media';
  }

  // Education
  if (industry.includes('education') || industry.includes('school') ||
      industry.includes('university')) {
    return 'education';
  }

  // Government (rare in SEC but possible)
  if (industry.includes('government') || industry.includes('public sector')) {
    return 'government';
  }

  // Default to empty string - user will select manually
  return '';
};

// ========================================
// Main Component
// ========================================

export const SmartDomainWizard: React.FC = () => {
  const navigate = useNavigate();
  const { addDomainTask } = useResearchQueueStore();
  const [isAddingToQueue, setIsAddingToQueue] = useState(false);
  const [currentStep, setCurrentStep] = useState<WizardStep>('context');
  const [context, setContext] = useState<CompanyContext>({
    company_name: '',
    industry: '',
    business_unit: '',
  });
  const [selectedDomains, setSelectedDomains] = useState<DomainSuggestion[]>([]);
  const [manualDomain, setManualDomain] = useState('');
  const [manualDomainDescription, setManualDomainDescription] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Subdomain selection state (per domain)
  const [domainSubdomains, setDomainSubdomains] = useState<Record<string, {
    selectedNames: string[];
    manualSubdomains: Array<{ name: string; description: string; scope: string; }>;
  }>>({});
  const [subdomainModalDomain, setSubdomainModalDomain] = useState<string | null>(null);

  // Company search state
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState<SECCompany[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Industry search state
  const [industrySearchQuery, setIndustrySearchQuery] = useState('');
  const [filteredIndustries, setFilteredIndustries] = useState<typeof INDUSTRIES>([]);
  const [showIndustryResults, setShowIndustryResults] = useState(false);
  const [secIndustryName, setSecIndustryName] = useState<string | null>(null); // Raw SEC industry name

  // ========================================
  // AI Suggestions Query
  // ========================================

  const suggestionsQuery = useQuery({
    queryKey: ['domain-suggestions', context],
    queryFn: () => suggestDomains({
      company_name: context.company_name,
      industry: context.industry,
      executive_questions: [], // Empty for domain research (used in case wizard)
      personas: [], // Empty for domain research (used in case wizard)
      business_unit: context.business_unit || undefined,
      top_k: 5,
    }),
    enabled: false, // Manual trigger
  });

  // ========================================
  // Company Search Logic (SEC API)
  // ========================================

  const performCompanySearch = useCallback(async (query: string) => {
    setIsSearching(true);
    try {
      const results = await standaloneCompaniesAPI.searchSECCompanies(query);
      const transformedResults = results.map((company: any) => ({
        cik: company.cik,
        name: company.name,
        ticker: company.ticker,
        exchange: company.exchange,
        match_type: company.match_type,
      }));
      setCompanySearchResults(transformedResults);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Company search failed:', error);
      setCompanySearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced company search
  useEffect(() => {
    if (companySearchQuery.trim().length < 3) {
      setCompanySearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const debounceTimer = setTimeout(() => {
      performCompanySearch(companySearchQuery.trim());
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [companySearchQuery, performCompanySearch]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('#company_name') && !target.closest('.search-results-dropdown')) {
        setShowSearchResults(false);
      }
      if (!target.closest('#industry_search') && !target.closest('.industry-results-dropdown')) {
        setShowIndustryResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter industries based on search query
  useEffect(() => {
    if (industrySearchQuery.trim().length === 0) {
      // Show all industries when input is empty but focused
      setFilteredIndustries(INDUSTRIES);
      return;
    }

    const query = industrySearchQuery.toLowerCase();
    const filtered = INDUSTRIES.filter(
      (industry) =>
        industry.label.toLowerCase().includes(query) ||
        industry.value.toLowerCase().includes(query)
    );
    setFilteredIndustries(filtered);
  }, [industrySearchQuery]);

  // Handle industry selection from dropdown
  const handleIndustrySelect = (industry: { value: string; label: string }) => {
    setIndustrySearchQuery(industry.label);
    setContext({ ...context, industry: industry.value });
    setShowIndustryResults(false);
    setSecIndustryName(null); // Clear SEC industry name when manually selecting
  };

  // Handle industry input change (free-form typing)
  const handleIndustryInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIndustrySearchQuery(value);
    setShowIndustryResults(true);

    // Check if the typed value matches any industry
    const matchedIndustry = INDUSTRIES.find(
      (ind) => ind.label.toLowerCase() === value.toLowerCase()
    );

    if (matchedIndustry) {
      setContext({ ...context, industry: matchedIndustry.value });
    } else {
      // For custom industries, use the typed value as-is
      setContext({ ...context, industry: value.toLowerCase().replace(/\s+/g, '-') });
    }

    // Clear SEC industry name when user types
    if (secIndustryName) {
      setSecIndustryName(null);
    }
  };

  // Handle company selection from search results
  const handleCompanySelect = async (company: SECCompany) => {
    setCompanySearchQuery(company.name);
    setShowSearchResults(false);

    // Fetch detailed company info including industry from SEC
    let industry = '';
    let rawSecIndustry = '';
    if (company.cik) {
      try {
        console.log('🔍 Fetching industry data for CIK:', company.cik);
        const basicInfo = await api.get(`/companies/sec/${company.cik}/basic-info`);

        // Store raw SEC industry name for display
        rawSecIndustry = basicInfo.industry || '';

        // Map SEC industry to our industry categories
        const secIndustry = basicInfo.industry?.toLowerCase() || '';
        industry = mapSECIndustryToCategory(secIndustry);

        console.log('✅ SEC Industry:', basicInfo.industry, '→ Mapped to:', industry);
      } catch (error) {
        console.error('❌ Failed to fetch industry data:', error);
        // Continue without industry - user can select manually
      }
    }

    // Update industry search field with the mapped industry label
    if (industry) {
      const matchedIndustry = INDUSTRIES.find((ind) => ind.value === industry);
      if (matchedIndustry) {
        setIndustrySearchQuery(matchedIndustry.label);
      } else {
        // Use raw SEC industry if no match found
        setIndustrySearchQuery(rawSecIndustry);
      }
      setSecIndustryName(rawSecIndustry); // Store SEC industry name for display
    }

    setContext({
      ...context,
      company_name: company.name,
      industry: industry, // Auto-populate if found, otherwise empty
    });
  };

  // ========================================
  // Generate Domain Pack Mutation
  // ========================================

  const generateMutation = useMutation({
    mutationFn: generateDomainPack,
    onSuccess: (data) => {
      navigate(`/domain-packs/${data.domain_pack_id}/progress?job_id=${data.job_id}`);
    },
    onError: (error: any) => {
      setErrors({
        submit: error.response?.data?.detail || 'Failed to start research',
      });
    },
  });

  // ========================================
  // Step 1: Company Context
  // ========================================

  const validateContext = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!context.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    if (!context.industry) {
      newErrors.industry = 'Industry is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContextNext = async () => {
    if (validateContext()) {
      // Trigger AI suggestions immediately
      await suggestionsQuery.refetch();
      setCurrentStep('suggestions');
    }
  };

  // ========================================
  // Step 2: AI Suggestions - Multi-Select
  // ========================================

  const handleToggleDomain = (suggestion: DomainSuggestion) => {
    setSelectedDomains(prev => {
      const isSelected = prev.some(d => d.domain_name === suggestion.domain_name);
      if (isSelected) {
        return prev.filter(d => d.domain_name !== suggestion.domain_name);
      } else {
        return [...prev, suggestion];
      }
    });
  };

  const handleAddManualDomain = () => {
    if (!manualDomain.trim()) {
      setErrors({ ...errors, manualDomain: 'Domain name is required' });
      return;
    }

    const newDomain: DomainSuggestion = {
      domain_pack_id: null,
      version_id: null,
      domain_name: manualDomain.trim(),
      industry: context.industry,
      industry_display: INDUSTRIES.find(i => i.value === context.industry)?.label || context.industry,
      research_available: false,
      variant_id: null,
      relevance_score: 1.0,
      reasoning: manualDomainDescription.trim() || 'Manually added domain',
      total_subdomains: 0,
      total_hypotheses: 0,
      quality_score: 0,
      relevant_subdomains: [],
      top_hypotheses: [],
    };

    setSelectedDomains(prev => [...prev, newDomain]);
    setManualDomain('');
    setManualDomainDescription('');
    setShowManualEntry(false);
    setErrors({});
  };

  const handleSuggestionsNext = () => {
    if (selectedDomains.length === 0) {
      setErrors({ ...errors, domains: 'Please select at least one domain or add manually' });
      return;
    }

    // Clear any previous errors
    setErrors({});

    // Always go to subdomain selection/confirmation step
    // This allows user to review all selected domains and choose subdomains if available
    setCurrentStep('subdomains');
  };

  // ========================================
  // Step 3: Subdomain Selection (per domain)
  // ========================================

  const handleOpenSubdomainModal = (domainName: string) => {
    setSubdomainModalDomain(domainName);
  };

  const handleCloseSubdomainModal = () => {
    setSubdomainModalDomain(null);
  };

  const handleConfirmSubdomains = (
    domainName: string,
    selectedNames: string[],
    manualSubdomains: Array<{ name: string; description: string; scope: string; }>
  ) => {
    setDomainSubdomains(prev => ({
      ...prev,
      [domainName]: {
        selectedNames,
        manualSubdomains,
      }
    }));
  };

  // ========================================
  // Step 4: Final Submit
  // ========================================

  const handleFinalSubmit = () => {
    if (selectedDomains.length === 0) return;

    // Separate domains into those with research vs those needing research
    const domainsWithResearch = selectedDomains.filter(d => d.research_available && d.domain_pack_id);
    const domainsNeedingResearch = selectedDomains.filter(d => !d.research_available || !d.domain_pack_id);

    // If only one domain and it has research, navigate directly to it
    if (selectedDomains.length === 1 && domainsWithResearch.length === 1) {
      navigate(`/domain-packs/${domainsWithResearch[0].domain_pack_id}`);
      return;
    }

    // For domains needing research, create research jobs for ALL of them
    if (domainsNeedingResearch.length > 0) {
      let jobsCreated = 0;
      let firstJobId: string | null = null;
      let firstDomainPackId: string | null = null;

      // Create research job for each domain with a delay between them
      domainsNeedingResearch.forEach((domain, index) => {
        setTimeout(() => {
          // Get selected subdomains for this specific domain
          const domainSubs = domainSubdomains[domain.domain_name];
          const allSubdomains = [
            ...(domainSubs?.selectedNames || []),
            ...(domainSubs?.manualSubdomains?.map(ms => ms.name) || [])
          ];

          generateMutation.mutate(
            {
              domain_name: domain.domain_name,
              description: domain.reasoning,
              industry_context: domain.industry_display,
              selected_subdomains: allSubdomains.length > 0 ? allSubdomains : undefined,
              force_regenerate: false,
            },
            {
              onSuccess: (data) => {
                jobsCreated++;

                // Save first job details for navigation
                if (index === 0) {
                  firstJobId = data.job_id;
                  firstDomainPackId = data.domain_pack_id;
                }

                // Navigate to first job's progress page when all jobs are created
                if (jobsCreated === domainsNeedingResearch.length && firstDomainPackId && firstJobId) {
                  navigate(`/domain-packs/${firstDomainPackId}/progress?job_id=${firstJobId}`);
                }
              },
              onError: (error: any) => {
                setErrors({
                  submit: `Failed to start research for "${domain.domain_name}": ${error.response?.data?.detail || error.message}`,
                });
              },
            }
          );
        }, index * 300); // 300ms delay between each request
      });

      // Show info message if multiple domains
      if (domainsNeedingResearch.length > 1) {
        setErrors({
          ...errors,
          info: `Creating research jobs for ${domainsNeedingResearch.length} domains: ${domainsNeedingResearch.map(d => `"${d.domain_name}"`).join(', ')}`,
        });
      }
    } else {
      // All domains have existing research - navigate to domain packs list
      navigate('/domain-packs');
    }
  };

  // Add domains to research queue instead of starting immediately
  const handleAddToQueue = async () => {
    if (selectedDomains.length === 0) return;

    setIsAddingToQueue(true);
    try {
      // Add each selected domain that needs research to the queue
      const domainsNeedingResearch = selectedDomains.filter(d => !d.research_available || !d.domain_pack_id);

      if (domainsNeedingResearch.length === 0) {
        toast.info('All selected domains already have research available');
        setIsAddingToQueue(false);
        return;
      }

      for (const domain of domainsNeedingResearch) {
        await addDomainTask({
          domainName: domain.domain_name,
          description: domain.reasoning,
          industry: domain.industry || context.industry,
        });
      }

      toast.success(
        `Added ${domainsNeedingResearch.length} domain${domainsNeedingResearch.length > 1 ? 's' : ''} to research queue`,
        { description: 'Research will start when queue is processed' }
      );

      // Navigate to queue page
      navigate('/research-queue');
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || 'Failed to add to queue';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setIsAddingToQueue(false);
    }
  };

  // ========================================
  // Render
  // ========================================

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-primary-500" />
            Smart Domain Pack Wizard
          </h1>
          <p className="text-neutral-600 mt-2">
            AI-powered domain research based on your company context
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/domain-packs')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Progress Steps */}
      <Card>
        <CardBody className="pt-6">
          <div className="flex items-center justify-between">
            {[
              { id: 'context', label: 'Company Context', icon: Building2 },
              { id: 'suggestions', label: 'AI Recommendations', icon: Brain },
              { id: 'subdomains', label: 'Subdomain Selection', icon: CheckCircle2 },
            ].map((step, index, array) => {
              const Icon = step.icon;
              const isCurrent = step.id === currentStep;
              const isComplete = array.findIndex(s => s.id === currentStep) > index;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isCurrent
                          ? 'bg-primary-500 text-white'
                          : isComplete
                          ? 'bg-green-500 text-white'
                          : 'bg-neutral-200 text-neutral-600'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <span
                      className={`text-sm mt-2 ${
                        isCurrent ? 'text-primary-600 font-semibold' : 'text-neutral-600'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index < array.length - 1 && (
                    <div
                      className={`h-0.5 flex-1 mx-4 mt-5 ${
                        isComplete ? 'bg-green-500' : 'bg-neutral-200'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* Step 1: Company Context */}
      {currentStep === 'context' && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Context
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              Tell us about your company to get personalized domain recommendations
            </p>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="relative">
              <Label htmlFor="company_name">Company Name *</Label>
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  id="company_name"
                  value={companySearchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCompanySearchQuery(e.target.value);
                    setContext({ ...context, company_name: e.target.value });
                  }}
                  onFocus={() => {
                    if (companySearchResults.length > 0) {
                      setShowSearchResults(true);
                    }
                  }}
                  placeholder="Search for company (e.g., Apple, Microsoft)"
                  className={`pl-10 ${errors.company_name ? 'border-red-500' : ''}`}
                />
                {isSearching && (
                  <Loader2 className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 animate-spin" />
                )}
              </div>
              {errors.company_name && (
                <p className="text-sm text-red-500 mt-1">{errors.company_name}</p>
              )}
              <p className="text-sm text-neutral-600 mt-2">
                Start typing to search SEC-registered companies (minimum 3 characters)
              </p>

              {/* Search Results Dropdown */}
              {showSearchResults && companySearchResults.length > 0 && (
                <div className="search-results-dropdown absolute z-10 w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {companySearchResults.map((company) => (
                    <button
                      key={company.cik}
                      type="button"
                      onClick={() => handleCompanySelect(company)}
                      className="w-full text-left px-4 py-3 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-neutral-900">{company.name}</p>
                          <p className="text-sm text-neutral-600">
                            {company.ticker} · {company.exchange}
                          </p>
                        </div>
                        <Badge variant="neutral" size="sm">{company.match_type}</Badge>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <Label htmlFor="industry_search">Industry *</Label>
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  id="industry_search"
                  value={industrySearchQuery}
                  onChange={handleIndustryInputChange}
                  onFocus={() => {
                    setFilteredIndustries(INDUSTRIES);
                    setShowIndustryResults(true);
                  }}
                  placeholder="Search or type industry (e.g., Technology, Healthcare)"
                  className={`pl-10 ${errors.industry ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.industry && (
                <p className="text-sm text-red-500 mt-1">{errors.industry}</p>
              )}
              {secIndustryName && (
                <p className="text-sm text-green-600 mt-1">
                  SEC Industry: {secIndustryName}
                </p>
              )}
              <p className="text-sm text-neutral-600 mt-2">
                Start typing to search industries or enter a custom industry
              </p>

              {/* Industry Search Results Dropdown */}
              {showIndustryResults && filteredIndustries.length > 0 && (
                <div className="industry-results-dropdown absolute z-10 w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {filteredIndustries.map((industry) => (
                    <button
                      key={industry.value}
                      type="button"
                      onClick={() => handleIndustrySelect(industry)}
                      className={`w-full text-left px-4 py-3 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 transition-colors ${
                        context.industry === industry.value ? 'bg-primary-50' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-neutral-900">{industry.label}</p>
                        </div>
                        {context.industry === industry.value && (
                          <CheckCircle2 className="h-4 w-4 text-primary-500" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="business_unit">Business Unit (Optional)</Label>
              <Input
                id="business_unit"
                value={context.business_unit}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContext({ ...context, business_unit: e.target.value })}
                placeholder="e.g., Hospital Operations, Supply Chain"
              />
              <p className="text-sm text-neutral-600 mt-2">
                Optional - helps AI provide more specific domain recommendations
              </p>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleContextNext} disabled={suggestionsQuery.isFetching}>
                {suggestionsQuery.isFetching ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Getting AI Recommendations...
                  </>
                ) : (
                  <>
                    Get Domain Recommendations
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Step 3: AI Suggestions */}
      {currentStep === 'suggestions' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <Brain className="h-5 w-5" />
                AI Recommendations
              </h3>
              <p className="text-sm text-neutral-600 mt-1">
                Based on your context, we recommend these domains
              </p>
            </CardHeader>
          </Card>

          {suggestionsQuery.isLoading && (
            <Card>
              <CardBody className="py-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary-500" />
                <p className="mt-4 text-neutral-600">Analyzing your context...</p>
              </CardBody>
            </Card>
          )}

          {suggestionsQuery.data?.map((suggestion, index) => {
            const isSelected = selectedDomains.some(d => d.domain_name === suggestion.domain_name);

            return (
              <Card
                key={index}
                className={`transition-all ${
                  isSelected
                    ? 'border-primary-500 border-2 shadow-lg'
                    : suggestion.research_available
                    ? 'border-green-500 border-2'
                    : 'border-amber-500 border-2'
                } hover:shadow-lg`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {/* Checkbox for selection */}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleDomain(suggestion)}
                        className="mt-1 h-5 w-5 rounded border-neutral-300 text-primary-500 focus:ring-primary-500 cursor-pointer"
                      />
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                          {suggestion.domain_name}
                          <Badge
                            variant={suggestion.research_available ? 'success' : 'warning'}
                          >
                            {Math.round(suggestion.relevance_score * 100)}% match
                          </Badge>
                        </h4>
                        <p className="text-sm text-neutral-600 mt-2">
                          {suggestion.industry_display}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {suggestion.research_available ? (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="font-semibold text-sm">Research Available</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertCircle className="h-5 w-5" />
                          <span className="font-semibold text-sm">Research Not Available</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <p className="text-neutral-700 mb-4">{suggestion.reasoning}</p>

                  {suggestion.research_available && (
                    <div className="flex gap-4 text-sm text-neutral-600">
                      <span>{suggestion.total_subdomains} subdomains</span>
                      <span>{suggestion.total_hypotheses} hypotheses</span>
                      {suggestion.quality_score > 0 && (
                        <span>Quality: {Math.round(suggestion.quality_score * 100)}%</span>
                      )}
                    </div>
                  )}

                  {!suggestion.research_available && (
                    <p className="text-sm text-amber-700 mt-2">
                      ⏱️ Estimated research time: 10-15 minutes
                    </p>
                  )}
                </CardBody>
              </Card>
            );
          })}

          {/* Manual Domain Entry */}
          <Card className="border-2 border-dashed border-neutral-300">
            <CardHeader>
              <Button
                variant="outline"
                onClick={() => setShowManualEntry(!showManualEntry)}
                className="w-full"
              >
                {showManualEntry ? 'Hide' : '+ Add Custom Domain'}
              </Button>
            </CardHeader>
            {showManualEntry && (
              <CardBody className="space-y-4">
                <div>
                  <Label htmlFor="manual_domain">Domain Name *</Label>
                  <Input
                    id="manual_domain"
                    value={manualDomain}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualDomain(e.target.value)}
                    placeholder="e.g., Supply Chain Management"
                    className={errors.manualDomain ? 'border-red-500' : ''}
                  />
                  {errors.manualDomain && (
                    <p className="text-sm text-red-500 mt-1">{errors.manualDomain}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="manual_domain_description">Description (Optional)</Label>
                  <textarea
                    id="manual_domain_description"
                    value={manualDomainDescription}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setManualDomainDescription(e.target.value)}
                    placeholder="Why this domain is important for your business"
                    className="w-full min-h-[100px] px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <Button onClick={handleAddManualDomain} className="w-full">
                  Add Domain
                </Button>
              </CardBody>
            )}
          </Card>

          {/* Selected Domains Summary */}
          {selectedDomains.length > 0 && (
            <Card className="border-2 border-primary-500 bg-primary-50">
              <CardHeader>
                <h4 className="text-lg font-semibold text-neutral-900">
                  Selected Domains ({selectedDomains.length})
                </h4>
                <p className="text-sm text-neutral-600 mt-1">
                  These domains will be used for research
                </p>
              </CardHeader>
              <CardBody>
                <div className="flex flex-wrap gap-2">
                  {selectedDomains.map((domain) => (
                    <Badge
                      key={domain.domain_name}
                      variant="primary"
                      className="flex items-center gap-2 px-3 py-2 text-base"
                    >
                      <span>{domain.domain_name}</span>
                      <button
                        onClick={() => handleToggleDomain(domain)}
                        className="ml-2 hover:text-red-500 transition-colors"
                        aria-label={`Remove ${domain.domain_name}`}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Validation Error */}
          {errors.domains && (
            <Card className="border-red-500">
              <CardBody className="pt-6">
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-5 w-5" />
                  <span>{errors.domains}</span>
                </div>
              </CardBody>
            </Card>
          )}

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={() => setCurrentStep('context')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleSuggestionsNext}
              disabled={selectedDomains.length === 0}
            >
              Continue with Selected Domains ({selectedDomains.length})
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Review & Confirm Domains */}
      {currentStep === 'subdomains' && selectedDomains.length > 0 && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                Review & Confirm Domains
              </h3>
              <p className="text-sm text-neutral-600 mt-1">
                Review your selected domains and choose subdomains where available
              </p>
            </CardHeader>
          </Card>

          {/* Domains with existing research - subdomain selection available */}
          {selectedDomains
            .filter(domain => domain.research_available && domain.relevant_subdomains && domain.relevant_subdomains.length > 0)
            .map((domain) => (
              <Card key={domain.domain_name} className="border-2 border-green-500">
                <CardHeader>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-neutral-900 mb-2 flex items-center gap-2">
                          {domain.domain_name}
                          <Badge variant="success">
                            Research Available
                          </Badge>
                        </h3>
                        <p className="text-sm text-neutral-600">{domain.reasoning}</p>
                      </div>
                      <div className="text-sm text-neutral-600">
                        {domain.total_subdomains} subdomains available
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <Button
                    variant="primary"
                    onClick={() => handleOpenSubdomainModal(domain.domain_name)}
                    className="w-full"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {domainSubdomains[domain.domain_name]
                      ? `Selected Subdomains (${(domainSubdomains[domain.domain_name]?.selectedNames?.length || 0) + (domainSubdomains[domain.domain_name]?.manualSubdomains?.length || 0)})`
                      : 'Get AI Suggestions & Select Subdomains'}
                  </Button>

                  {domainSubdomains[domain.domain_name] && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-semibold text-green-900 mb-2">
                        ✓ {(domainSubdomains[domain.domain_name]?.selectedNames?.length || 0) + (domainSubdomains[domain.domain_name]?.manualSubdomains?.length || 0)} subdomain(s) selected
                      </p>
                      <div className="space-y-1 mt-2">
                        {domainSubdomains[domain.domain_name]?.selectedNames?.map((name, idx) => (
                          <div key={`selected-${idx}`} className="flex items-center gap-2 text-sm text-green-800">
                            <span className="w-1.5 h-1.5 bg-green-600 rounded-full flex-shrink-0"></span>
                            <span>{name}</span>
                          </div>
                        ))}
                        {domainSubdomains[domain.domain_name]?.manualSubdomains?.map((sub, idx) => (
                          <div key={`manual-${idx}`} className="flex items-center gap-2 text-sm text-green-800">
                            <span className="w-1.5 h-1.5 bg-green-600 rounded-full flex-shrink-0"></span>
                            <span>{sub.name} <span className="text-xs text-green-600">(Custom)</span></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            ))}

          {/* Domains WITHOUT research - will create new research */}
          {selectedDomains
            .filter(domain => !domain.research_available || !domain.relevant_subdomains || domain.relevant_subdomains.length === 0)
            .map((domain) => (
              <Card key={domain.domain_name} className="border-2 border-amber-500">
                <CardHeader>
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-neutral-900 mb-2 flex items-center gap-2">
                          {domain.domain_name}
                          <Badge variant="warning">
                            New Research Required
                          </Badge>
                        </h3>
                        <p className="text-sm text-neutral-600">{domain.reasoning}</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="flex items-start gap-2 text-amber-700 mb-4">
                    <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">New research will be initiated for this domain</p>
                      <p className="text-sm mt-1">
                        ⏱️ Estimated time: 10-15 minutes
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="primary"
                    onClick={() => handleOpenSubdomainModal(domain.domain_name)}
                    className="w-full"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {domainSubdomains[domain.domain_name]
                      ? `Selected Subdomains (${(domainSubdomains[domain.domain_name]?.selectedNames?.length || 0) + (domainSubdomains[domain.domain_name]?.manualSubdomains?.length || 0)})`
                      : 'Get AI Suggestions & Select Subdomains'}
                  </Button>

                  {domainSubdomains[domain.domain_name] && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-semibold text-green-900 mb-2">
                        ✓ {(domainSubdomains[domain.domain_name]?.selectedNames?.length || 0) + (domainSubdomains[domain.domain_name]?.manualSubdomains?.length || 0)} subdomain(s) selected
                      </p>
                      <div className="space-y-1 mt-2">
                        {domainSubdomains[domain.domain_name]?.selectedNames?.map((name, idx) => (
                          <div key={`selected-${idx}`} className="flex items-center gap-2 text-sm text-green-800">
                            <span className="w-1.5 h-1.5 bg-green-600 rounded-full flex-shrink-0"></span>
                            <span>{name}</span>
                          </div>
                        ))}
                        {domainSubdomains[domain.domain_name]?.manualSubdomains?.map((sub, idx) => (
                          <div key={`manual-${idx}`} className="flex items-center gap-2 text-sm text-green-800">
                            <span className="w-1.5 h-1.5 bg-green-600 rounded-full flex-shrink-0"></span>
                            <span>{sub.name} <span className="text-xs text-green-600">(Custom)</span></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            ))}

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={() => setCurrentStep('suggestions')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex gap-3">
              {/* Show Add to Queue button only when there are domains needing research */}
              {selectedDomains.some(d => !d.research_available || !d.domain_pack_id) && (
                <Button
                  variant="outline"
                  onClick={handleAddToQueue}
                  disabled={isAddingToQueue || generateMutation.isPending}
                  title="Add to background queue for batch processing"
                >
                  {isAddingToQueue ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <ListPlus className="h-4 w-4 mr-2" />
                      Add to Queue
                    </>
                  )}
                </Button>
              )}
              <Button onClick={handleFinalSubmit} disabled={generateMutation.isPending || isAddingToQueue}>
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating Research Jobs...
                  </>
                ) : (
                  <>
                    {(() => {
                      const domainsNeedingResearch = selectedDomains.filter(d => !d.research_available || !d.domain_pack_id);
                      const domainsWithResearch = selectedDomains.filter(d => d.research_available && d.domain_pack_id);

                      if (domainsNeedingResearch.length > 0 && domainsWithResearch.length > 0) {
                        return `Confirm & Start Research (${domainsNeedingResearch.length} New)`;
                      } else if (domainsNeedingResearch.length > 0) {
                        return `Start Research (${domainsNeedingResearch.length} Domain${domainsNeedingResearch.length > 1 ? 's' : ''})`;
                      } else {
                        return `View Existing Research (${domainsWithResearch.length} Domain${domainsWithResearch.length > 1 ? 's' : ''})`;
                      }
                    })()}
                    <CheckCircle2 className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Info Display */}
      {errors.info && (
        <Card className="border-blue-500">
          <CardBody className="pt-6">
            <div className="flex items-center gap-2 text-blue-600">
              <AlertCircle className="h-5 w-5" />
              <span>{errors.info}</span>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Error Display */}
      {errors.submit && (
        <Card className="border-red-500">
          <CardBody className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <span>{errors.submit}</span>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Subdomain Selection Modal */}
      {subdomainModalDomain && selectedDomains.find(d => d.domain_name === subdomainModalDomain) && (() => {
        const domain = selectedDomains.find(d => d.domain_name === subdomainModalDomain)!;
        return (
          <WizardSubdomainSelectModal
            domainName={domain.domain_name}
            industry={context.industry}
            isOpen={true}
            onClose={handleCloseSubdomainModal}
            onConfirm={(selectedNames, manualSubdomains) => {
              handleConfirmSubdomains(domain.domain_name, selectedNames, manualSubdomains);
            }}
            existingSubdomains={domain.research_available && domain.relevant_subdomains ? domain.relevant_subdomains : undefined}
          />
        );
      })()}
    </div>
  );
};
